import React from 'react';
import { useBrand } from '@/context/BrandContext';
import { EditableText } from '../ui/EditableText';

interface BrandSectionLayoutProps {
  section: any;
  isEditing: boolean;
  isGlobalDarkMode: boolean;
  editingElement: string | null;
  setEditingElement: (element: string | null) => void;
  updateSectionContent: (key: string, value: string) => void;
  updateSectionStyles: (key: string, styles: any) => void;
  children: React.ReactNode;
  showPageNumber?: boolean;
  pageNumber?: number;
  isCoverPage?: boolean;
}

export const BrandSectionLayout: React.FC<BrandSectionLayoutProps> = ({
  section,
  isEditing,
  isGlobalDarkMode,
  editingElement,
  setEditingElement,
  updateSectionContent,
  updateSectionStyles,
  children,
  showPageNumber = true,
  pageNumber = 1,
  isCoverPage = false
}) => {
  const { state } = useBrand();
  const currentYear = new Date().getFullYear().toString();
  const formattedPageNumber = `PAGE ${pageNumber.toString().padStart(2, '0')}`;

  const textColor = isCoverPage ? '#ffffff' : '#000000';

  return (
    <div className="h-full w-full" style={{ minHeight: '100vh', padding: '40px 50px' }}>
      <div className="h-full flex flex-col" style={{ minHeight: 'calc(100vh - 80px)' }}>

        {/* Header Section */}
        <div className="w-full pb-2 mb-8 border-b" style={{ borderColor: isCoverPage ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.15)' }}>
          <div className="flex items-center justify-between">
            {/* Favicon */}
            <div className="flex items-center">
              {state.brandData?.favicon ? (
                <div className="w-10 h-10 rounded-full flex-shrink-0 overflow-hidden">
                  <img
                    src={state.brandData.favicon}
                    alt="Brand Favicon"
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="w-10 h-10 rounded-full bg-gray-300 flex-shrink-0 flex items-center justify-center">
                  <span className="text-xs text-gray-600">Icon</span>
                </div>
              )}
            </div>

            {/* Brand Guideline */}
            <div className="flex items-center">
              <EditableText
                value={section.content.headerBrandGuideline || '{{brandName}}/BRAND GUIDELINE'}
                onChange={(value) => updateSectionContent('headerBrandGuideline', value)}
                element="span"
                className="text-sm font-medium uppercase"
                placeholder="{{brandName}}/BRAND GUIDELINE"
                editModeEnabled={isEditing}
                isEditing={isEditing && editingElement === 'headerBrandGuideline'}
                onEditStart={() => setEditingElement('headerBrandGuideline')}
                onEditEnd={() => setEditingElement(null)}
                onStyleChange={(styles) => updateSectionStyles('headerBrandGuideline', styles)}
                initialStyles={{
                  color: textColor,
                  fontFamily: state.brandData?.globalTextFont || 'Inter',
                  fontSize: 14,
                  textTransform: 'uppercase',
                  ...section.content.headerBrandGuidelineStyles
                }}
                isGlobalDarkMode={isGlobalDarkMode}
                sectionBackgroundColor={section.customization?.backgroundColor || section.styles.backgroundColor}
              />
            </div>

            {/* Year */}
            <div className="flex items-center">
              <EditableText
                value={section.content.headerYear || currentYear}
                onChange={(value) => updateSectionContent('headerYear', value)}
                element="span"
                className="text-sm font-medium uppercase"
                placeholder="YEAR"
                editModeEnabled={isEditing}
                isEditing={isEditing && editingElement === 'headerYear'}
                onEditStart={() => setEditingElement('headerYear')}
                onEditEnd={() => setEditingElement(null)}
                onStyleChange={(styles) => updateSectionStyles('headerYear', styles)}
                initialStyles={{
                  color: textColor,
                  fontFamily: state.brandData?.globalTextFont || 'Inter',
                  fontSize: 14,
                  textTransform: 'uppercase',
                  ...section.content.headerYearStyles
                }}
                isGlobalDarkMode={isGlobalDarkMode}
                sectionBackgroundColor={section.customization?.backgroundColor || section.styles.backgroundColor}
              />
            </div>

            {/* Brand Credits */}
            <div className="flex items-center">
              <EditableText
                value={section.content.headerCredits || 'DESIGNED BY {{designCredit}}'}
                onChange={(value) => updateSectionContent('headerCredits', value)}
                element="span"
                className="text-sm font-medium uppercase"
                placeholder="DESIGNED BY {{designCredit}}"
                editModeEnabled={isEditing}
                isEditing={isEditing && editingElement === 'headerCredits'}
                onEditStart={() => setEditingElement('headerCredits')}
                onEditEnd={() => setEditingElement(null)}
                onStyleChange={(styles) => updateSectionStyles('headerCredits', styles)}
                initialStyles={{
                  color: textColor,
                  fontFamily: state.brandData?.globalTextFont || 'Inter',
                  fontSize: 14,
                  textTransform: 'uppercase',
                  ...section.content.headerCreditsStyles
                }}
                isGlobalDarkMode={isGlobalDarkMode}
                sectionBackgroundColor={section.customization?.backgroundColor || section.styles.backgroundColor}
              />
            </div>
          </div>
        </div>

        {/* Main Content - Takes remaining space */}
        <div className="flex-1 overflow-auto">
          {children}
        </div>

        {/* Footer Section */}
        <div className="w-full pt-2 mt-8 border-t" style={{ borderColor: isCoverPage ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.15)' }}>
          <div className="flex items-center justify-between">
            {/* Brand Guideline */}
            <div className="flex items-center">
              <EditableText
                value={section.content.footerBrandGuideline || '{{brandName}}/BRAND GUIDELINE'}
                onChange={(value) => updateSectionContent('footerBrandGuideline', value)}
                element="span"
                className="text-sm font-medium uppercase"
                placeholder="{{brandName}}/BRAND GUIDELINE"
                editModeEnabled={isEditing}
                isEditing={isEditing && editingElement === 'footerBrandGuideline'}
                onEditStart={() => setEditingElement('footerBrandGuideline')}
                onEditEnd={() => setEditingElement(null)}
                onStyleChange={(styles) => updateSectionStyles('footerBrandGuideline', styles)}
                initialStyles={{
                  color: textColor,
                  fontFamily: state.brandData?.globalTextFont || 'Inter',
                  fontSize: 14,
                  textTransform: 'uppercase',
                  ...section.content.footerBrandGuidelineStyles
                }}
                isGlobalDarkMode={isGlobalDarkMode}
                sectionBackgroundColor={section.customization?.backgroundColor || section.styles.backgroundColor}
              />
            </div>

            {/* Page Number */}
            {showPageNumber && (
              <div className="flex items-center">
                <EditableText
                  value={section.content.pageNumber || formattedPageNumber}
                  onChange={(value) => updateSectionContent('pageNumber', value)}
                  element="span"
                  className="text-sm font-medium uppercase"
                  placeholder={formattedPageNumber}
                  editModeEnabled={isEditing}
                  isEditing={isEditing && editingElement === 'pageNumber'}
                  onEditStart={() => setEditingElement('pageNumber')}
                  onEditEnd={() => setEditingElement(null)}
                  onStyleChange={(styles) => updateSectionStyles('pageNumber', styles)}
                  initialStyles={{
                    color: textColor,
                    fontFamily: state.brandData?.globalTextFont || 'Inter',
                    fontSize: 14,
                    textTransform: 'uppercase',
                    ...section.content.pageNumberStyles
                  }}
                  isGlobalDarkMode={isGlobalDarkMode}
                  sectionBackgroundColor={section.customization?.backgroundColor || section.styles.backgroundColor}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
